import { ColumnDef } from '@tanstack/react-table'
import { cn } from '@/libs/utils'
import { MoreHorizontal, Edit } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DataTableColumnHeader } from '@/components/crud'
import { Organization } from './schema'

export const createColumns = (
  t: (key: string) => string,
  onEdit?: (organization: Organization) => void
): ColumnDef<Organization>[] => [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label={t('organizations.selectAll')}
        className='translate-y-[2px]'
      />
    ),
    meta: {
      className: cn(
        'sticky md:table-cell left-0 z-10 rounded-tl',
        'bg-background transition-colors duration-200 group-hover/row:bg-muted group-data-[state=selected]/row:bg-muted'
      ),
    },
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label={t('organizations.selectRow')}
        className='translate-y-[2px]'
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('common.name')} />
    ),
    cell: ({ row }) => {
      return (
        <div className='flex space-x-2'>
          <span className='max-w-[200px] truncate font-medium'>
            {row.getValue('name')}
          </span>
        </div>
      )
    },
    enableSorting: true,
    enableHiding: false,
  },
  {
    accessorKey: 'isEnterprise',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t('organizations.isEnterprise')}
      />
    ),
    cell: ({ row }) => {
      const isEnterprise = row.getValue('isEnterprise') as boolean
      return (
        <div className='flex space-x-2'>
          <span
            className={`rounded-full px-2 py-1 text-xs font-medium ${
              isEnterprise
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
            }`}
          >
            {isEnterprise
              ? t('organizations.enterprise')
              : t('organizations.standard')}
          </span>
        </div>
      )
    },
    enableSorting: true,
  },
  {
    accessorKey: 'createdBy',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('common.createdBy')} />
    ),
    cell: ({ row }) => {
      const createdBy = row.getValue('createdBy') as string
      return (
        <div className='flex space-x-2'>
          <span className='max-w-[150px] truncate text-sm text-muted-foreground'>
            {createdBy || '-'}
          </span>
        </div>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('common.createdAt')} />
    ),
    cell: ({ row }) => {
      const date = row.getValue('createdAt') as string
      return (
        <div className='flex w-[100px] items-center'>
          <span>{date ? new Date(date).toLocaleDateString() : '-'}</span>
        </div>
      )
    },
    enableSorting: true,
  },
  {
    id: 'actions',
    header: () => null,
    cell: ({ row }) => {
      const organization = row.original
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' className='h-8 w-8 p-0'>
              <span className='sr-only'>{t('common.openMenu')}</span>
              <MoreHorizontal className='h-4 w-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onEdit?.(organization)}>
              <Edit className='mr-2 h-4 w-4' />
              {t('common.edit')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    },
    enableSorting: false,
    enableHiding: false,
  },
]
